<template>
  <div style="width: 100%; height: 100%; display: flex; flex-direction: column">
    <!-- ================= CONTACT MEDIA ================= -->
    <div style="flex: 1; overflow-y: auto; padding: 16px">
      <div v-if="!Loading && !checkContactID()" class="text-grey">
        Contact is not an Open Dental patient.
      </div>

      <div v-else>
        <h3>Contact Media</h3>

        <v-tabs v-model="mediaTab" density="compact">
          <v-tab value="received">Received</v-tab>
          <v-tab value="sent">Sent</v-tab>
        </v-tabs>

        <div v-if="filteredMediaList.length === 0 && !Loading" class="text-grey">
          No media available.
        </div>
        <template v-if="Loading">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="32"
            ></v-progress-circular>
          </div>
        </template>
        <v-card
          v-for="item in filteredMediaList"
          :key="item.media_id"
          class="media-card"
          elevation="2"
        >
          <v-chip
            v-if="item.conversation_id"
            size="x-small"
            label
            class="mb-1"
            :prepend-icon="item.source === 'email' ? 'mdi-email-outline' : 'mdi-message-text-outline'"
            @click="goToMessage(item)"
          >
            {{ item.source === 'email' ? 'Email' : 'Text' }}
          </v-chip>

          <div class="media-left">

  <!--FALLBACK -->
  <div v-if="item.mediaError" class="file-preview generic">
    <v-icon size="32" color="red">mdi-file-alert</v-icon>
    <span>Missing</span>
  </div>
          
            <!-- IMAGE -->
            <v-img
              v-else-if="item.is_image"
              :src="item.url"
              class="media-preview"
              cover
              @click="openFile(item)"
              @error="handleMediaError(item)"
            />
          
            <!-- VIDEO -->
            <video
              v-else-if="item.mime_type?.startsWith('video/')"
              class="media-preview media-video"
              controls
              :src="item.url"
              @error="handleMediaError(item)"
            ></video>
          
            <!-- PDF -->
            <div
              v-else-if="item.mime_type === 'application/pdf'"
              @click="openFile(item)"
              class="file-preview pdf"
            >
              <v-icon size="32" color="red">mdi-file-pdf-box</v-icon>
              <span>PDF</span>
            </div>
          
            <!-- WORD -->
            <div
              v-else-if="item.mime_type?.includes('word')"
              @click="openFile(item)"
              class="file-preview word"
            >
              <v-icon size="32" color="blue">mdi-file-word</v-icon>
              <span>DOC</span>
            </div>
          
            <!-- GENERIC -->
            <div
              v-else
              class="file-preview generic"
              @click="openFile(item)"
            >
              <v-icon size="32" color="grey">mdi-file</v-icon>
              <span>FILE</span>
            </div>
          
          </div>


          <!-- UPLOAD STATUS / REVIEW -->
          <div
            v-if="!item.mediaError"
            class="d-flex align-center"
            style="gap: 6px; font-size: 12px; margin-top: 4px; cursor: pointer"
            @click.stop="openExtractionDialog(item)"
          >
            <template v-if="item.upload_status === 'uploaded'">
              <v-icon size="16" color="green">mdi-check-circle-outline</v-icon>
              <span>Uploaded</span>
            </template>
            <template v-else-if="item.upload_status === 'failed'">
              <v-icon size="16" color="error">mdi-alert-circle-outline</v-icon>
              <span>Upload failed — review</span>
            </template>
            <template v-else>
              <v-icon size="16" color="primary">mdi-text-box-search-outline</v-icon>
              <span>Review and upload to OD</span>
            </template>
          </div>

          <!--  NEW MEDIA NOTE FEATURE -->
          <div v-if="shouldDisplayMediaNote(item)" class="media-note-container">
            <!-- READ MODE -->
            <div
              v-if="!item.isEditingMediaNote"
              @click.stop="startMediaNoteEdit(item)"
              style="
                display: flex;
                align-items: center;
                gap: 6px;
                font-size: 12px;
                background: #f4f6f8;
                padding: 6px 8px;
                border-radius: 6px;
                cursor: pointer;
              "
            >
              <v-icon size="14">mdi-note-text</v-icon>
              <span class="media-note-text">
                {{ formatMediaNote(item) }}
              </span>
            </div>
            <!-- EDIT MODE -->
            <div v-else style="width: 100%; flex: content">
              <v-text-field
                dense
                outlined
                label="Description"
                v-model="item.mediaEditedDescription"
              />

              <v-select
                dense
                outlined
                label="Category"
                :items="categories"
                item-title="name"
                item-value="id"
                v-model="item.mediaEditedCategory"
                :loading="categoryLoading"
                :disabled="categoryLoading"
                @click="onCategoryOpen"
              />

              <div style="display: flex; justify-content: flex-end; gap: 8px">
                <v-icon
                  size="18"
                  color="green"
                  @click.stop="saveMediaNote(item)"
                >
                  mdi-check
                </v-icon>

                <v-icon
                  size="18"
                  color="red"
                  @click.stop="cancelMediaNoteEdit(item)"
                >
                  mdi-close
                </v-icon>
              </div>
            </div>
          </div>
        </v-card>
      </div>
    </div>

    <!-- ================= ACTION BAR ================= -->
    <div
      v-if="checkContactID()"
      style="border-top: 1px solid #ddd; padding: 12px; background: #fff"
    >
      <div style="display: flex; gap: 10px; margin-bottom: 10px">
        <v-btn stacked variant="plain" @click="openCamera">
          <v-icon left>mdi-camera</v-icon>
        </v-btn>

        <v-file-input
          v-model="pickedFiles"
          multiple
          hide-details
          label="Attach files"
          :loading="staging"
        />
      </div>

      <v-dialog v-model="cameraDialog" max-width="520">
        <v-card title="Camera">
          <v-card-text>
            <div style="position: relative">
              <video
                ref="video"
                autoplay
                muted
                playsinline
                style="width: 100%; border-radius: 8px"
              ></video>

              <v-icon
                v-if="isCameraOpen"
                class="camera-button"
                size="64"
                color="white"
                @click="capturePhoto"
              >
                mdi-circle-slice-8
              </v-icon>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer />
            <v-btn text @click="closeCamera">Close</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <canvas ref="canvas" style="display: none"></canvas>
    </div>

    <DocumentExtractionDialog
      v-model="extractionDialogOpen"
      :media-id="extractionMediaId"
      :contact-id="contact?.contact_id"
    />
  </div>
</template>

<script>
import MiscService from "@/services/miscService";
import OdService from "@/services/odService";
import EventBus from "@/eventBus";
import { ALERT } from "../../../constants/alert";
import odService from "../../../services/odService";
import { mapActions } from "vuex";
import DocumentExtractionDialog from "./DocumentExtractionDialog.vue";

export default {
  components: { DocumentExtractionDialog },

  props: { contact: Object },

  data() {
    return {
      mediaList: [],
      pickedFiles: null,
      staging: false,
      categories: [],
      Loading: false,
      categoryLoading: false,
      categoriesLoaded: false,
      cameraDialog: false,
      isCameraOpen: false,
      stream: null,
      mediaTab: "received",
      extractionDialogOpen: false,
      extractionMediaId: null,
    };
  },

  computed: {
    filteredMediaList() {
      return this.mediaList.filter((item) => item.direction === this.mediaTab);
    },
  },

  mounted() {
    this.onCategoryOpen();
  },

  watch: {
    'contact.contact_id': {
    immediate: true,
    handler(newId, oldId) {

      if (!newId || newId === oldId) return;

      this.mediaList = [];

      this.refreshMedia();
    }
  },
    pickedFiles(files) {
      if (!files || !files.length) return;
      this.stageFiles(files);
      this.pickedFiles = null;
    },
  },

  methods: {
    ...mapActions("frontdesk", ["searchConversation"]),
    checkContactID() {
      return this.contact?.OD_PatNum;
    },
    // Jumps to this media item's message in the center panel — mirrors
    // FDMessenger.vue's tagconversation() jump: load the messages around
    // that timestamp first (the virtualized list won't have every message
    // rendered), then ask FDMessenger (already listening globally) to
    // scroll/highlight the matching bubble.
    async goToMessage(item) {
      if (!item.conversation_id) return;
      await this.searchConversation({
        timestamp: item.conversation_timestamp,
        contact_id: this.contact.contact_id
      });
      EventBus.$emit("scroll-to-reference", item.conversation_id);
    },
    // get patient recevied, media from backend
    async refreshMedia() {
      if (!this.checkContactID()) {
        return;
      }

      this.Loading = true;

      try {
        const res = await MiscService.getMessageMediaForContact(
          this.contact.contact_id,
        );

        this.mediaList = res.data.data.map((i) => ({
          ...i,
          uploaded: Boolean(i.uploaded),
          mime_type: i.content_type,
          is_image: i.content_type?.startsWith("image/"),
          // Absolute URL, not a relative "/api/..." path: in local dev the
          // frontend (vue-cli-service, :8081) and backend (:3000) are
          // separate origins with no devServer proxy configured, so a
          // relative path would resolve against the frontend dev server,
          // which has no such route. Only works as a relative path in
          // production, where the backend serves the built frontend from
          // the same origin.
          url: `${process.env.VUE_APP_API_URL}/api/contact/${this.contact.contact_id}/media/${i.media_id}`,

          isEditingMediaNote: false,
          mediaEditedDescription: i.Description || "",
          mediaEditedCategory: i.DocCategory || "",
          mediaError: false, 
        }));

      } catch (err) {
        console.error("Failed to load media:", err);
      } finally {
        this.Loading = false;
      }
    },
  handleMediaError(item) {
  console.warn("Media file missing:", item.media_id);
  item.mediaError = true;
  },

    openExtractionDialog(item) {
      this.extractionMediaId = item.media_id;
      this.extractionDialogOpen = true;
    },

    // to display media Description and  DocCategory only if all fields are present
    shouldDisplayMediaNote(item) {
      return !!item.DocNum;
    },

    formatMediaNote(item) {
      return `${item.DocCategory} — ${item.Description}`;
    },

    startMediaNoteEdit(item) {
      item.isEditingMediaNote = true;
    },

    cancelMediaNoteEdit(item) {
      item.isEditingMediaNote = false;
      item.mediaEditedDescription = item.Description;
      item.mediaEditedCategory = item.DocCategory;
    },
    async saveMediaNote(item) {
      let matchedCategory;

      const isNumeric = Number.isFinite(Number(item.mediaEditedCategory));

      if (isNumeric) {
        matchedCategory = this.categories.find(
          (c) => c.id === Number(item.mediaEditedCategory),
        );
      } else {
        matchedCategory = this.categories.find(
          (c) => c.name === item.mediaEditedCategory,
        );

        if (matchedCategory) {
          item.mediaEditedCategory = matchedCategory.id;
        }
      }

      if (!matchedCategory) {
        EventBus.$emit("alert", {
          message: "Invalid category selected",
          type: ALERT.WARNING,
        });
        return;
      }

      const payload = {
        media_id: item.media_id,
        DocNum: item.DocNum,
        Description: item.mediaEditedDescription,
        DocCategory: Number(item.mediaEditedCategory),
      };

      try {
        const res = await odService.updateDocuments(payload);
        if (!res || res.status !== 200) {
          EventBus.$emit("alert", {
            message: "Failed to update media note",
            type: ALERT.ERROR,
          });
          return;
        }

        item.Description = item.mediaEditedDescription;
        item.DocCategory = matchedCategory.name;
        item.isEditingMediaNote = false;
      } catch (err) {
        console.error("Error updating media note:", err);
        EventBus.$emit("alert", {
          message: "Failed to update media note",
          type: ALERT.ERROR,
        });
      }
    },

    async openCamera() {
      try {
        this.cameraDialog = true;
        await this.$nextTick();

        this.stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
          audio: false,
        });

        this.$refs.video.srcObject = this.stream;
        this.isCameraOpen = true;
      } catch {
        EventBus.$emit("alert", {
          message: "Camera access failed",
          type: ALERT.ERROR,
        });
      }
    },

    closeCamera() {
      this.stopCamera();
      this.cameraDialog = false;
    },

    stopCamera() {
      if (this.stream) {
        this.stream.getTracks().forEach((t) => t.stop());
        this.stream = null;
      }
      this.isCameraOpen = false;
    },

    capturePhoto() {
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      canvas.getContext("2d").drawImage(video, 0, 0);

      canvas.toBlob((blob) => {
        const file = new File([blob], `camera_${Date.now()}.jpg`, {
          type: "image/jpeg",
        });
        this.stageFiles([file]);
      }, "image/jpeg");
    },

    async onCategoryOpen() {
      if (this.categoriesLoaded || this.categoryLoading) return;

      this.categoryLoading = true;
      try {
        const res = await OdService.getImageCategories();
        this.categories = (res.data || []).map((c) => ({
          id: c.Id,
          name: c.Name,
        }));
        this.categoriesLoaded = true;
      } finally {
        this.categoryLoading = false;
      }
    },

    // Uploads new files (camera capture or file-picker) immediately for
    // staging + classification, instead of holding them locally for manual
    // category/description entry. They reappear via refreshMedia() with
    // their own "Review and upload to OD" link, same as any AI-classified
    // inbound document.
    async stageFiles(files) {
      if (!files || !files.length) return;
      this.staging = true;
      try {
        const formData = new FormData();
        files.forEach((file) => formData.append("files", file));
        formData.append("contact_id", this.contact.contact_id);

        await OdService.stageDocuments(formData);

        EventBus.$emit("alert", {
          message: "Files added — review and upload to OD below.",
          type: ALERT.SUCCESS,
        });

        await this.refreshMedia();
      } catch (err) {
        console.error("Staging error:", err);
        EventBus.$emit("alert", {
          message: "Some files failed to upload",
          type: ALERT.ERROR,
        });
      } finally {
        this.staging = false;
      }
    },
  },

  beforeUnmount() {
    this.stopCamera();
  },
};
</script>

<style scoped>
.camera-button {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
}

.media-card {
  display: flex;
  align-items: flex-start;
  padding: 12px;
  margin-bottom: 10px;
  border-radius: 10px;
}

.media-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.media-preview {
  width: 90px;
  height: 70px;
  border-radius: 8px;
  cursor: pointer;
  object-fit: cover;
}

.media-video {
  background: #000;
}

.file-preview {
  width: 90px;
  height: 70px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
}
.media-note-container {
  flex: 1; /* takes remaining space */
  min-width: 250px; /* prevents squishing */
  max-width: 500px; /* optional */
}

.media-note-text {
  white-space: normal; /* allow wrapping */
  word-break: break-word; /* break long text */
}
</style>
